import { LoadingOutlined, UserOutlined } from '@ant-design/icons';
import { useOidc } from '@axa-fr/react-oidc';
import { useIsMutating, useTranslation } from '@threatadvisor/react-utils';
import { Breadcrumb, Layout, Menu, Space, Spin } from 'antd';
import { createStyles, css } from 'antd-style';
import { ItemType, MenuItemType } from 'antd/es/menu/interface';
import React, { ReactElement, Suspense } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import AppNameBanner from '../AppNameBanner/AppNameBanner';
import { useTheme } from '../ThemeProvider';
import { useUserContext } from '../oidc/UserProvider';
import { useBreadCrumbContext } from './BreadCrumbContext';

const { Header, Content } = Layout;

const useStyles = createStyles((props: { isDarkMode: boolean }) => {
  return {
    layoutEl: css`
      height: 100vh;
      @media print {
        height: unset;
      }
    `,
    contentEl: css`
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      padding: 15px 0px 15px 15px;
      overflow: hidden;
      @media print {
        padding: unset;
      }
    `,
    menuStyle: {
      backgroundColor: props.isDarkMode ? '#1f1f1f' : '#fff',
      boxShadow: props.isDarkMode
        ? '-6px 0 5px 0px rgba(0, 0, 0, 0.4)'
        : '-6px 0 5px 0px rgba(100, 100, 100, 0.2)',
      position: 'sticky',
      display: 'flex',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ['@media print' as any]: {
        display: 'none'
      },
      paddingLeft: 'unset',
      'ant-table-footer': {
        // Adjust table footer to make it same height as sidebar menu collapse button
        paddingTop: '13px',
        paddingBottom: '13px'
      }
    },
    breadCrumbs: css`
      margin-left: '25px';
      margin-top: '8px';

      @media print {
        display: none;
      }
    `
  };
});

export type TopLevelMenuProps = {
  children: ItemType<MenuItemType>[];
  extras?: ReactElement;
  additionalUserLinks?: ItemType<MenuItemType>[];
  title: string;
  baseUrl?: string;
  extrasBeforeAccount?: ReactElement;
  isLoading?: boolean;
  useBreadcrumbNav?: boolean;
  accountUrl?: string | undefined;
};

function TopLevelMenu(props: TopLevelMenuProps): ReactElement {
  const {
    title,
    children,
    extras,
    additionalUserLinks,
    baseUrl,
    extrasBeforeAccount,
    isLoading,
    useBreadcrumbNav,
    accountUrl
  } = props;

  const { t } = useTranslation();
  const { logout } = useOidc('config_show_access_token');
  const navigate = useNavigate();
  const { breadCrumbs } = useBreadCrumbContext();

  const { isDarkMode } = useTheme();
  const { cx, styles } = useStyles({ isDarkMode });

  const { oidcUser } = useUserContext();
  const isMutating = useIsMutating();

  const onLogoutClick = () => {
    const _returnUrl = `${window.location.protocol}//${window.location.hostname}${
      window.location.port ? `:${window.location.port}` : null
    }`;
    // ?returnUrl=${returnUrl} fails at OpenIddictServerHandlers.Session.cs line 522
    oidcUser && logout(`${baseUrl}/connect/logout`);
  };

  return (
    <Layout className={cx(styles.layoutEl)}>
      <Header className={cx(styles.menuStyle)}>
        <AppNameBanner title={title} portalUrl={baseUrl ?? '/'} />
        {useBreadcrumbNav ? (
          <Breadcrumb
            style={{
              display: 'flex',
              flex: 1,
              alignSelf: 'center',
              marginLeft: '8px'
            }}
            items={breadCrumbs}
            separator={'/'}
          />
        ) : (
          <Menu mode="horizontal" items={children} style={{ flex: 'auto', minWidth: 0 }}></Menu>
        )}
        {extras}
        <Menu
          disabledOverflow={true}
          mode="horizontal"
          items={[
            ...(extrasBeforeAccount
              ? [
                  {
                    key: 'extras',
                    label: extrasBeforeAccount
                  }
                ]
              : []),
            {
              key: 'sm-account',
              label: <UserOutlined style={{ paddingLeft: '10px' }} />,
              style: { paddingLeft: '10px' },
              children: [
                {
                  key: 'account',
                  label: t('labels.account', 'Account'),
                  onClick: () => {
                    navigate(accountUrl ? accountUrl : '/account');
                  }
                },
                ...(additionalUserLinks ?? []),
                {
                  key: 'log-out',
                  label: t('labels.logOut', 'Log Out'),
                  onClick: onLogoutClick
                }
              ]
            }
          ]}
        />
        <div style={{ width: 20, paddingLeft: 10 }}>{isMutating > 0 && <LoadingOutlined />}</div>
      </Header>
      {useBreadcrumbNav ? null : (
        <div className={cx(styles.breadCrumbs)}>
          <Breadcrumb items={breadCrumbs} separator={'>'} />
        </div>
      )}
      <Content className={cx(styles.contentEl)}>
        <Suspense
          fallback={
            <Space className="spinner">
              <Spin />
            </Space>
          }
        >
          {isLoading ? (
            <Space className="spinner">
              <Spin />
            </Space>
          ) : (
            <Outlet />
          )}
        </Suspense>
      </Content>
    </Layout>
  );
}

export default React.memo(TopLevelMenu);
